<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { submitOrder } from '$lib/api/order';
  import { queryFilters, resultStats } from '$lib/stores/selection';
  import { selectedIndustryCentroids } from '$lib/stores/data';
  import { neighborSamples } from '$lib/stores/neighbors';
  import { filteredMixedResults } from '$lib/stores/mixedResults';
  import { calculateTotalPrice, pricingTiers, getBillableRecordCount } from '$lib/pricing/pricing';
  import { derived } from 'svelte/store';
  import Obfuscated from '$lib/components/Obfuscated.svelte';
  import { dataInClear } from '$lib/stores/auth';
  import { getRoleLabel } from '$lib/utils/roleMapping';
  import { swissRound, formatSwissFrancs } from '$lib/utils/swissRounding';

  import type { ApplicationMode } from '$lib/stores/mode';
  import { applicationMode } from '$lib/stores/mode';
  import { countries, getCountryName } from '$lib/data/countries';

  import { previousMode, orderDetails } from '$lib/stores/checkout';
  import { track } from '$lib/analytics/tracking';
  import { pixel } from '$lib';
  import { ValidationError } from '$lib/utils/error-handling';
  import ScreenView from '$lib/components/ScreenView.svelte';
  import { CheckCircle2, ClipboardCheck, ClipboardList, UserRound } from 'lucide-svelte';

  export let selectedMode = $applicationMode;

  const selectMode = (mode: ApplicationMode) => {
    if (mode !== selectedMode) {
      selectedMode = mode;
      applicationMode.set(mode);
    }
  };

  const returnToOriginalMode = () => {
    selectMode($previousMode);
  };

  // Customer data
  let customer = {
    company: '',
    salutation: '',
    firstName: '',
    lastName: '',
    email: '',
    street: '',
    house: '',
    zip: '',
    city: '',
    country: 'CH',
    agreed: false
  };

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Track if form has been touched
  let formTouched = false;
  let hasStartedFilling = false;

  // Track which fields have been touched
  let touchedFields = new Set<string>();

  // Mark field as touched
  function touchField(fieldName: string) {
    touchedFields.add(fieldName);
    touchedFields = touchedFields; // Trigger reactivity
    formTouched = true;

    // Track first interaction with form
    if (!hasStartedFilling) {
      hasStartedFilling = true;
    }
  }

  // Validate single field
  function validateField(fieldName: string, value: string) {
    if (!touchedFields.has(fieldName) && !formTouched) return;

    switch (fieldName) {
      case 'company':
        if (!value?.trim()) {
          fieldErrors[fieldName] = $_('form.errors.companyRequired');
        } else {
          delete fieldErrors[fieldName];
        }
        break;
      case 'firstName':
        if (!value?.trim()) {
          fieldErrors[fieldName] = $_('form.errors.firstNameRequired');
        } else {
          delete fieldErrors[fieldName];
        }
        break;
      case 'lastName':
        if (!value?.trim()) {
          fieldErrors[fieldName] = $_('form.errors.lastNameRequired');
        } else {
          delete fieldErrors[fieldName];
        }
        break;
      case 'email':
        if (!value?.trim()) {
          fieldErrors[fieldName] = $_('form.errors.emailRequired');
        } else if (!emailRegex.test(value)) {
          fieldErrors[fieldName] = $_('form.errors.emailInvalid');
        } else {
          delete fieldErrors[fieldName];
        }
        break;
      case 'street':
        if (!value?.trim()) {
          fieldErrors[fieldName] = $_('form.errors.streetRequired');
        } else {
          delete fieldErrors[fieldName];
        }
        break;
      case 'zip':
        if (!value?.trim()) {
          fieldErrors[fieldName] = $_('form.errors.zipRequired');
        } else {
          delete fieldErrors[fieldName];
        }
        break;
      case 'city':
        if (!value?.trim()) {
          fieldErrors[fieldName] = $_('form.errors.cityRequired');
        } else {
          delete fieldErrors[fieldName];
        }
        break;
    }

    fieldErrors = fieldErrors; // Trigger reactivity
  }

  // Validate all fields
  function validateForm() {
    formTouched = true;

    validateField('company', customer.company);
    validateField('firstName', customer.firstName);
    validateField('lastName', customer.lastName);
    validateField('email', customer.email);
    validateField('street', customer.street);
    validateField('zip', customer.zip);
    validateField('city', customer.city);
  }

  // Watch for changes and validate individual fields
  $: validateField('company', customer.company);
  $: validateField('firstName', customer.firstName);
  $: validateField('lastName', customer.lastName);
  $: validateField('email', customer.email);
  $: validateField('street', customer.street);
  $: validateField('zip', customer.zip);
  $: validateField('city', customer.city);

  $: isFormValid =
    customer.company &&
    customer.firstName &&
    customer.lastName &&
    customer.street &&
    customer.zip &&
    customer.city &&
    customer.email &&
    emailRegex.test(customer.email) &&
    customer.agreed;

  let isSubmitting = false;
  /** Validation messages, keyed by form field name. */
  let fieldErrors: Record<string, string> = {};
  let generalError = '';
  let showConfirmation = false;
  let orderPlaced = false;

  /**
   * Which of the two steps is on screen.
   *
   * The order details and the customer's own details were two columns of one
   * screen, which reads well in a wide dialog and nowhere else: below `lg`
   * they stacked, so the form began somewhere under the fold of a summary the
   * customer had not been asked to read yet.
   *
   * As two steps each gets the width, in the order the customer thinks in —
   * this is what you are buying, then this is who you are.
   */
  let step: 1 | 2 = 1;

  function showOrderConfirmation() {
    formTouched = true;
    validateForm();

    if (!isFormValid) {
      // Focus on first error field
      const firstError = Object.keys(fieldErrors)[0];
      if (firstError) {
        const errorField = document.querySelector<HTMLElement>(`[name="${firstError}"]`);
        if (errorField) errorField.focus();
      }
      return;
    }

    showConfirmation = true;
  }

  function goBackToForm() {
    showConfirmation = false;
    step = 2;
  }

  async function placeOrder() {
    if (!isFormValid || isSubmitting) return;

    isSubmitting = true;
    fieldErrors = {};
    generalError = '';

    // Convert current UI `customer` to expected CustomerInfo format
    const fullCustomer = {
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email,
      company: customer.company,
      salutation: customer.salutation,
      address: {
        street: customer.street,
        house: customer.house,
        city: customer.city,
        zipCode: customer.zip,
        country: customer.country
      }
    };

    try {
      const orderResponse = await submitOrder(fullCustomer);

      // Track successful order completion
      const orderId = orderResponse?.orderId || 'TEST-' + Date.now();

      // Track via our tracking module
      track.orderCompleted({
        orderId: orderId,
        mode: $previousMode,
        companyCount: $orderSummary.companyCount,
        contactCount: $orderSummary.contactCount,
        totalValue: $orderSummary.total,
        customerEmail: fullCustomer.email
      });

      // Track purchase with Meta Pixel
      pixel.trackConversion('Purchase', {
        value: $orderSummary.total,
        currency: 'CHF',
        content_type: 'product',
        content_ids: ['company_data'],
        contents: [
          {
            id: 'company_data',
            quantity: $orderSummary.companyCount,
            item_price: $orderSummary.total / Math.max($orderSummary.companyCount, 1)
          }
        ],
        num_items: $orderSummary.companyCount
      });

      // Store order details and show success view
      const orderData = {
        orderId: orderResponse.orderId || 'TEST-' + Date.now(),
        customer: fullCustomer,
        companyCount: $orderSummary.companyCount,
        contactCount: $orderSummary.contactCount,
        total: $orderSummary.total
      };
      orderDetails.set(orderData);
      orderPlaced = true;
    } catch (err: unknown) {
      // `submitOrder` throws ValidationError for a bad field and an ordinary
      // Error otherwise; a catch clause sees `unknown` either way.
      if (err instanceof ValidationError) {
        fieldErrors[err.field] = err.message;
      } else {
        generalError = err instanceof Error ? err.message : $_('form.errors.orderFailed');
      }

      // Don't set orderPlaced = true on error!
    } finally {
      isSubmitting = false;
    }
  }

  function cancelOrder() {
    step = 1;
    returnToOriginalMode();
  }

  // Create proper pricing calculation and summary data
  const orderSummary = derived(
    [
      queryFilters,
      resultStats,
      selectedIndustryCentroids,
      filteredMixedResults,
      neighborSamples,
      applicationMode
    ],
    ([$filters, , $selectedLists, $filteredMixedResults, $neighborSamples, $applicationMode]) => {
      // Use centralized function to get billable record count
      const billableRecordCount = getBillableRecordCount($filteredMixedResults);
      const subtotal = calculateTotalPrice(pricingTiers, billableRecordCount);
      const vatRate = 0.081; // 8.1% Swiss VAT
      const vatAmount = swissRound(subtotal * vatRate);
      const total = swissRound(subtotal + vatAmount);

      // Calculate actual company and contact counts from filteredMixedResults
      const companyCount = $filteredMixedResults ? $filteredMixedResults.length : 0;
      const contactCount = $filteredMixedResults
        ? $filteredMixedResults.reduce((total, company) => {
            return total + (company.contacts?.length || 0);
          }, 0)
        : 0;

      // Calculate pricing breakdown by tier
      const pricingBreakdown = [];
      let remaining = billableRecordCount;
      let currentStart = 1;

      for (let i = 0; i < pricingTiers.length && remaining > 0; i++) {
        const tier = pricingTiers[i];
        const tierLimit = tier.count === Infinity ? remaining : tier.count;
        const unitsInThisTier = Math.min(remaining, tierLimit);

        if (unitsInThisTier > 0) {
          const tierSubtotal = swissRound(unitsInThisTier * tier.price);
          pricingBreakdown.push({
            range:
              tier.count === Infinity
                ? `${currentStart}+`
                : `${currentStart}-${currentStart + unitsInThisTier - 1}`,
            quantity: unitsInThisTier,
            unitPrice: tier.price,
            subtotal: tierSubtotal
          });
          remaining -= unitsInThisTier;
          currentStart += unitsInThisTier;
        }
      }

      return {
        selectedLists: $selectedLists,
        filters: $filters,
        neighborSamples: $neighborSamples,
        applicationMode: $applicationMode,
        recordCount: billableRecordCount,
        companyCount,
        contactCount,
        pricingBreakdown,
        subtotal,
        vatAmount,
        total
      };
    }
  );
</script>

{#if showConfirmation}
  <!-- Confirmation View -->
  <ScreenView
    icon={orderPlaced ? CheckCircle2 : ClipboardCheck}
    title={orderPlaced ? $_('headings.orderReceived') : $_('headings.confirmOrder')}
  >
    <button
      slot="actions"
      type="button"
      on:click={orderPlaced ? returnToOriginalMode : goBackToForm}
      class="rounded-control px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    >
      {$_('buttons.close')}
    </button>

    {#if !orderPlaced}
      <!-- Order Confirmation View -->

      <!-- General Error Message -->
      {#if generalError}
        <div class="mb-4 rounded-control border border-red-200 bg-red-50 p-4">
          <div class="flex items-center">
            <span class="text-sm text-red-800">{generalError}</span>
          </div>
        </div>
      {/if}

      <div class="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <!-- LEFT COLUMN: CUSTOMER & ORDER INFO -->
        <div class="space-y-3">
          <!-- Customer Details Summary -->
          <div class="mb-3 rounded-surface border border-hairline bg-white p-3 sm:p-4">
            <h3 class="mb-2 flex items-center text-xs font-semibold text-gray-900 sm:text-sm">
              {$_('form.labels.customerInformation')}
            </h3>
            <div class="space-y-0.5 text-xs">
              <div class="flex justify-between">
                <span class="text-gray-600">{$_('form.labels.company')}:</span>
                <span class="font-medium">{customer.company}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">{$_('form.labels.contact')}:</span>
                <span class="font-medium"
                  >{customer.salutation} {customer.firstName} {customer.lastName}</span
                >
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">{$_('form.labels.email')}:</span>
                <span class="font-medium">{customer.email}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">{$_('form.labels.address')}:</span>
                <span class="font-medium"
                  >{customer.street}
                  {customer.house}, {customer.zip}
                  {customer.city}, {getCountryName(customer.country)}</span
                >
              </div>
            </div>
          </div>

          <!--
            The same figures the overview shows, written the same way.

            This screen had them as two coloured tiles while the step before it
            had them as a line of text, so the last thing a customer read before
            paying disagreed in shape with the thing they had just read. The
            roles are resolved here too — the confirmation printed "technology"
            at somebody who chose "Leiter/in Technik".
          -->
          <div class="mb-3 rounded-surface border border-hairline bg-white p-3 sm:p-4">
            <h3 class="mb-2 flex items-center text-xs font-semibold text-gray-900 sm:text-sm">
              {$_('headings.orderSummary')}
            </h3>
            <div class="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-gray-700">
              <span>
                <span class="font-semibold tabular-nums text-gray-900"
                  >{$orderSummary.companyCount?.toLocaleString() || 0}</span
                >
                {$_('stats.companies')}
              </span>
              <span aria-hidden="true" class="text-gray-300">·</span>
              <span>
                <span class="font-semibold tabular-nums text-gray-900"
                  >{$orderSummary.contactCount?.toLocaleString() || 0}</span
                >
                {$_('stats.contacts')}
              </span>
            </div>

            {#if $orderSummary.filters.contact_roles && $orderSummary.filters.contact_roles.length > 0}
              <div class="mt-2 border-t border-hairline pt-2 text-2xs sm:text-xs">
                <span class="text-gray-600">{$_('filterLabels.contactRole')}:</span>
                <span class="font-medium text-gray-900">
                  {$orderSummary.filters.contact_roles
                    .map((role) => getRoleLabel(role, $_))
                    .join(' → ')}
                </span>
              </div>
            {/if}
          </div>

          <!-- What happens after the order is placed, said plainly. -->
          <div class="rounded-control border border-hairline bg-surface-canvas p-3">
            <p class="text-xs text-gray-600">{$_('order.emailNextSteps')}</p>
          </div>
        </div>

        <!-- RIGHT COLUMN: PRICING BREAKDOWN -->
        <div class="space-y-4">
          <!-- Pricing Details -->
          <div class="rounded-surface border border-hairline bg-white p-4">
            {#if $orderSummary.pricingBreakdown.length > 0}
              <!-- Table for pricing breakdown -->
              <div class="overflow-x-auto">
                <table class="w-full text-xs sm:text-sm">
                  <thead>
                    <tr class="border-b border-hairline">
                      <th class="py-1 text-left text-2xs font-medium text-gray-600 sm:text-xs"
                        >{$_('pricing.priceLevel')}</th
                      >
                      <th class="py-1 text-center text-2xs font-medium text-gray-600 sm:text-xs"
                        >{$_('pricing.amount')}</th
                      >
                      <th class="py-1 text-right text-2xs font-medium text-gray-600 sm:text-xs"
                        >{$_('pricing.pricePerPiece')}</th
                      >
                      <th class="py-1 text-right text-2xs font-medium text-gray-600 sm:text-xs"
                        >{$_('pricing.total')}</th
                      >
                    </tr>
                  </thead>
                  <tbody>
                    {#each $orderSummary.pricingBreakdown as tier}
                      <tr class="border-b border-hairline">
                        <td class="py-1 text-2xs text-gray-600 sm:text-xs">Records {tier.range}</td>
                        <td class="py-1 text-center text-2xs sm:text-xs">{tier.quantity}</td>
                        <td class="py-1 text-right text-2xs sm:text-xs"
                          >CHF {formatSwissFrancs(tier.unitPrice)}</td
                        >
                        <td class="py-1 text-right text-2xs font-medium sm:text-xs"
                          >CHF {formatSwissFrancs(tier.subtotal)}</td
                        >
                      </tr>
                    {/each}
                  </tbody>
                  <tfoot>
                    <tr class="border-t border-hairline-strong">
                      <td colspan="3" class="py-1 text-2xs text-gray-600 sm:text-xs"
                        >{$_('pricing.subtotal')}</td
                      >
                      <td class="py-1 text-right text-2xs font-medium sm:text-xs"
                        >CHF {formatSwissFrancs($orderSummary.subtotal)}</td
                      >
                    </tr>
                    <tr>
                      <td colspan="3" class="py-1 text-2xs text-gray-600 sm:text-xs"
                        >{$_('pricing.vat')}</td
                      >
                      <td class="py-1 text-right text-2xs font-medium sm:text-xs"
                        >CHF {formatSwissFrancs($orderSummary.vatAmount)}</td
                      >
                    </tr>
                    <tr class="border-t border-hairline-strong">
                      <td colspan="3" class="py-1 text-xs font-bold sm:py-2 sm:text-sm"
                        >{$_('pricing.total')}</td
                      >
                      <td class="py-1 text-right text-xs font-bold text-gray-900 sm:py-2 sm:text-sm"
                        >CHF {formatSwissFrancs($orderSummary.total)}</td
                      >
                    </tr>
                  </tfoot>
                </table>
              </div>
            {/if}
          </div>
        </div>
      </div>
    {:else}
      <!-- Order Success View -->
      <div class="mx-auto max-w-2xl space-y-6">
        <!--
          No second title, and no empty circle.

          The heading here repeated the screen's own title word for word, under
          a 20px grey disc that used to hold a tick and kept its size after the
          icon was stripped — so the screen opened on a blank circle and said
          the same thing twice. The header carries the mark and the title now;
          what is left to say is what happens next.
        -->
        <p class="text-sm leading-relaxed text-gray-600">
          {$_('status.orderSubmitted')}
        </p>

        <!-- What Happens Next -->
        <div class="rounded-surface border border-hairline bg-surface-canvas p-5 sm:p-6">
          <h3 class="mb-4 text-sm font-bold text-gray-900 sm:text-base">
            {$_('headings.whatHappensNext')}
          </h3>

          <div class="space-y-4">
            <div class="flex items-start gap-3">
              <div
                class="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-surface-sunken text-xs font-semibold text-gray-600"
              >
                1
              </div>
              <p class="text-sm leading-relaxed text-gray-700">
                {$_('order.confirmationEmail', { values: { email: customer.email } })}
              </p>
            </div>
            <div class="flex items-start gap-3">
              <div
                class="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-surface-sunken text-xs font-semibold text-gray-600"
              >
                2
              </div>
              <p class="text-sm leading-relaxed text-gray-700">{$_('order.teamReview')}</p>
            </div>
            <div class="flex items-start gap-3">
              <div
                class="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-surface-sunken text-xs font-semibold text-gray-600"
              >
                3
              </div>
              <p class="text-sm leading-relaxed text-gray-700">{$_('order.personalContact')}</p>
            </div>
          </div>
        </div>
      </div>
    {/if}

    <svelte:fragment slot="footer">
      {#if !orderPlaced}
        <button
          type="button"
          on:click={goBackToForm}
          class="rounded-control border border-hairline px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-hairline-strong hover:text-gray-900"
        >
          {$_('buttons.cancel')}
        </button>
        <button
          type="button"
          on:click={placeOrder}
          disabled={isSubmitting}
          class="rounded-control bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
        >
          {#if isSubmitting}
            <svg class="mr-2 inline h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {$_('status.loading')}...
          {:else}
            {$_('buttons.placeOrder')}
          {/if}
        </button>
      {:else}
        <button
          type="button"
          on:click={returnToOriginalMode}
          class="rounded-control bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
        >
          {$_('buttons.close')}
        </button>
      {/if}
    </svelte:fragment>
  </ScreenView>
{:else}
  <!-- Order Form -->
  <ScreenView
    icon={step === 1 ? ClipboardList : UserRound}
    title={$_('order.completeYourOrder')}
    {step}
    steps={2}
  >
    <button
      slot="actions"
      type="button"
      on:click={cancelOrder}
      class="rounded-control px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    >
      {$_('buttons.close')}
    </button>

    <!-- Step 1: what is being bought. Step 2: who is buying it. -->
    <!-- LEFT COLUMN: ORDER SUMMARY -->
    {#if step === 1}
      <div class="space-y-3 sm:space-y-4">
        <div>
          <h2 class="mb-2 text-base font-bold text-gray-900 sm:mb-3 sm:text-lg">
            {$_('headings.orderSummary')}
          </h2>

          <!--
            One card, not two.

            The selected lists and the totals were separate panels, numbered 1
            and 2, saying two halves of one sentence: this is what you picked,
            and this is how much of it there is. Nobody reads those as separate
            questions, and split across two bordered boxes the second one
            arrived with no subject.

            The counts are text now rather than two coloured tiles. They were
            the largest thing on a screen whose job is to let somebody check
            what they are about to buy — and the price, which is the number that
            actually decides the order, sits in the column beside them.
          -->
          <div class="mb-3 rounded-surface border border-hairline bg-white p-3 sm:mb-4 sm:p-4">
            <h3 class="mb-2 flex items-center text-sm font-semibold text-gray-900 sm:text-base">
              <span class="mr-2 text-xs font-bold text-gray-500 sm:text-sm">1.</span>
              {$_('modes.selectedData')}
            </h3>

            {#if $orderSummary.selectedLists && $orderSummary.selectedLists.size > 0}
              <div class="mb-3 flex flex-wrap gap-1.5">
                {#each [...$orderSummary.selectedLists] as [id, name] (id)}
                  <span
                    class="inline-flex items-center rounded-control bg-surface-sunken px-2 py-0.5 text-2xs text-gray-700 sm:text-xs"
                  >
                    {name}
                  </span>
                {/each}
              </div>
            {/if}

            <div class="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-gray-700">
              <span>
                <span class="font-semibold tabular-nums text-gray-900"
                  >{$orderSummary.companyCount?.toLocaleString() || 0}</span
                >
                {$_('stats.companies')}
              </span>
              <span aria-hidden="true" class="text-gray-300">·</span>
              <span>
                <span class="font-semibold tabular-nums text-gray-900"
                  >{$orderSummary.contactCount?.toLocaleString() || 0}</span
                >
                {$_('stats.contacts')}
              </span>
            </div>

            {#if $orderSummary.filters.contact_roles && $orderSummary.filters.contact_roles.length > 0}
              <div class="mt-2 border-t border-hairline pt-2 text-2xs sm:text-xs">
                <span class="text-gray-600">{$_('filterLabels.contactRole')}:</span>
                <span class="font-medium text-gray-900">
                  {$orderSummary.filters.contact_roles
                    .map((role) => getRoleLabel(role, $_))
                    .join(' → ')}
                </span>
              </div>
            {/if}
          </div>

          <!-- 2. Applied Filters Summary -->
          <div class="mb-3 rounded-surface border border-hairline bg-white p-3 sm:mb-4 sm:p-4">
            <h3 class="mb-2 flex items-center text-sm font-semibold text-gray-900 sm:text-base">
              <span class="mr-2 text-xs font-bold text-gray-500 sm:text-sm">2.</span>
              {#if $orderSummary.applicationMode === 'neighbor'}
                {$_('headings.similarCompanies')}
              {:else}
                {$_('buttons.applyFilters')}
              {/if}
            </h3>

            {#if $orderSummary.applicationMode === 'neighbor' && $orderSummary.neighborSamples.length > 0}
              <!-- Neighbor Mode: Show sample companies -->
              <div class="space-y-2">
                {#each $orderSummary.neighborSamples as sample}
                  <div
                    class="flex items-start gap-2 rounded-control border border-hairline bg-surface-canvas p-2"
                  >
                    <div
                      class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-surface-sunken"
                    ></div>
                    <div class="min-w-0 flex-1">
                      <div class="truncate text-2xs font-medium text-gray-900 sm:text-xs">
                        <Obfuscated
                          name={sample.name || 'Unknown Company'}
                          masked={!$dataInClear}
                        />
                      </div>
                    </div>
                  </div>
                {/each}
                <div class="mt-2 rounded-control border border-hairline bg-surface-canvas p-2">
                  <div class="flex items-center text-2xs text-gray-600 sm:text-xs">
                    AI found companies similar to these {$orderSummary.neighborSamples.length} examples
                  </div>
                </div>
              </div>
            {:else}
              <!-- Classic Mode: Show traditional filters -->
              <div class="grid gap-1 text-2xs sm:text-xs">
                {#if $orderSummary.filters.postal_radius.center_postal_code && $orderSummary.filters.postal_radius.radius_km > 0}
                  <div class="flex justify-between">
                    <span class="text-gray-600">{$_('filterLabels.postalRadius')}:</span>
                    <span class="font-medium"
                      >{$orderSummary.filters.postal_radius.center_postal_code} + {$orderSummary
                        .filters.postal_radius.radius_km}km</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.postal_codes && $orderSummary.filters.postal_codes.length > 0}
                  <div class="flex items-start justify-between">
                    <span class="text-gray-600">{$_('filterLabels.postalCode')}:</span>
                    <span class="text-right font-medium"
                      >{$orderSummary.filters.postal_codes.join(', ')}</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.cantons && $orderSummary.filters.cantons.length > 0}
                  <div class="flex items-start justify-between">
                    <span class="text-gray-600">{$_('filterLabels.cantons')}:</span>
                    <span class="text-right font-medium"
                      >{$orderSummary.filters.cantons.join(', ')}</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.company_size_range[0] !== null || $orderSummary.filters.company_size_range[1] !== null}
                  <div class="flex justify-between">
                    <span class="text-gray-600">{$_('filterLabels.employees')}:</span>
                    <span class="font-medium"
                      >{$orderSummary.filters.company_size_range[0] ?? ''}–{$orderSummary.filters
                        .company_size_range[1] ?? ''}</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.founding_year_range[0] !== null || $orderSummary.filters.founding_year_range[1] !== null}
                  <div class="flex justify-between">
                    <span class="text-gray-600">{$_('filters.founding.year')}:</span>
                    <span class="font-medium"
                      >{$orderSummary.filters.founding_year_range[0] ?? ''}–{$orderSummary.filters
                        .founding_year_range[1] ?? ''}</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.structured_filters.branch_office_count.min > 0}
                  <div class="flex justify-between">
                    <span class="text-gray-600">{$_('filterLabels.branches')}:</span>
                    <span class="font-medium"
                      >≥ {$orderSummary.filters.structured_filters.branch_office_count.min}</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.structured_filters.legal_form && $orderSummary.filters.structured_filters.legal_form.length > 0}
                  <div class="flex items-start justify-between">
                    <span class="text-gray-600">{$_('data.legalForm')}:</span>
                    <span class="text-right font-medium"
                      >{$orderSummary.filters.structured_filters.legal_form.join(', ')}</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.structured_filters.capital !== null && $orderSummary.filters.structured_filters.capital > 0}
                  <div class="flex justify-between">
                    <span class="text-gray-600">{$_('data.capital')}:</span>
                    <span class="font-medium"
                      >≥ CHF {$orderSummary.filters.structured_filters.capital.toLocaleString()}</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.structured_filters.revision_company}
                  <div class="flex justify-between">
                    <span class="text-gray-600">{$_('data.auditedBy')}:</span>
                    <span class="font-medium"
                      >{$orderSummary.filters.structured_filters.revision_company}</span
                    >
                  </div>
                {/if}
                <!-- Boolean filters as badges -->
                {#if $orderSummary.filters.structured_filters.has_website || $orderSummary.filters.structured_filters.has_email || $orderSummary.filters.structured_filters.has_phone || $orderSummary.filters.structured_filters.has_facebook || $orderSummary.filters.structured_filters.has_linkedin || $orderSummary.filters.structured_filters.has_instagram || $orderSummary.filters.structured_filters.has_twitter || $orderSummary.filters.structured_filters.has_revisionsverzicht}
                  <div class="space-y-2">
                    <span class="text-gray-600">{$_('order.summaryProperties')}:</span>
                    <div class="flex flex-wrap gap-1">
                      {#if $orderSummary.filters.structured_filters.has_website}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-600 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{$_('data.website')}</span
                        >
                      {/if}
                      {#if $orderSummary.filters.structured_filters.has_email}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-600 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{$_('data.email')}</span
                        >
                      {/if}
                      {#if $orderSummary.filters.structured_filters.has_phone}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-600 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{$_('data.phone')}</span
                        >
                      {/if}
                      {#if $orderSummary.filters.structured_filters.has_facebook}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-600 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{$_('data.facebook')}</span
                        >
                      {/if}
                      {#if $orderSummary.filters.structured_filters.has_linkedin}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-600 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{$_('data.linkedin')}</span
                        >
                      {/if}
                      {#if $orderSummary.filters.structured_filters.has_instagram}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-700 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{$_('data.instagram')}</span
                        >
                      {/if}
                      {#if $orderSummary.filters.structured_filters.has_twitter}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-600 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{$_('data.twitter')}</span
                        >
                      {/if}
                      {#if $orderSummary.filters.structured_filters.has_revisionsverzicht}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-600 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{$_('data.auditExemption')}</span
                        >
                      {/if}
                    </div>
                  </div>
                {/if}
                {#if $orderSummary.filters.text_constraints && $orderSummary.filters.text_constraints.length > 0}
                  <div class="space-y-2">
                    <span class="text-gray-600">{$_('order.summaryTextSearch')}:</span>
                    <div class="flex flex-wrap gap-1">
                      {#each $orderSummary.filters.text_constraints as term}
                        <span
                          class="inline-flex items-center rounded-control bg-surface-sunken px-1.5 py-0.5 text-2xs font-medium text-gray-700 ring-1 ring-inset ring-hairline/10 sm:text-xs"
                          >{term}</span
                        >
                      {/each}
                    </div>
                  </div>
                {/if}
                {#if $orderSummary.filters.embedding_query && $orderSummary.filters.embedding_query.trim() !== ''}
                  <div class="space-y-0.5">
                    <span class="text-gray-600">{$_('order.summaryAiSearch')}:</span>
                    <p class="text-2xs italic text-gray-700 sm:text-xs">
                      "{$orderSummary.filters.embedding_query}"
                    </p>
                  </div>
                {/if}
                {#if $orderSummary.filters.contact_roles.length > 0}
                  <div class="flex items-start justify-between">
                    <span class="text-gray-600">{$_('filterLabels.contactRoles')}:</span>
                    <span class="text-right font-medium"
                      >{$orderSummary.filters.contact_roles
                        .map((role) => getRoleLabel(role, $_))
                        .join(', ')}</span
                    >
                  </div>
                {/if}
                {#if $orderSummary.filters.contact_mode && $orderSummary.contactCount > 0}
                  <div class="flex justify-between">
                    <span class="text-gray-600">{$_('order.summaryContactMode')}:</span>
                    <span class="font-medium"
                      >{$orderSummary.filters.contact_mode === 'single'
                        ? $_('filters.contact.onePerRole')
                        : $_('filters.contact.allPerRole')}</span
                    >
                  </div>
                {/if}
              </div>
            {/if}
          </div>

          <!-- 4. Pricing Breakdown -->
          <div class="rounded-surface border border-hairline bg-white p-3 sm:p-4">
            <h3
              class="mb-2 flex items-center text-sm font-semibold text-gray-900 sm:mb-3 sm:text-base"
            >
              <span class="mr-2 text-xs font-bold text-gray-500 sm:text-sm">4.</span>
              {$_('headings.detailedBreakdown')}
            </h3>

            {#if $orderSummary.pricingBreakdown.length > 0}
              <!-- Table for pricing breakdown -->
              <div class="overflow-x-auto">
                <table class="w-full text-xs sm:text-sm">
                  <thead>
                    <tr class="border-b border-hairline">
                      <th class="py-1 text-left text-2xs font-medium text-gray-600 sm:text-xs"
                        >{$_('pricing.priceLevel')}</th
                      >
                      <th class="py-1 text-center text-2xs font-medium text-gray-600 sm:text-xs"
                        >{$_('pricing.amount')}</th
                      >
                      <th class="py-1 text-right text-2xs font-medium text-gray-600 sm:text-xs"
                        >{$_('pricing.pricePerPiece')}</th
                      >
                      <th class="py-1 text-right text-2xs font-medium text-gray-600 sm:text-xs"
                        >{$_('pricing.total')}</th
                      >
                    </tr>
                  </thead>
                  <tbody>
                    {#each $orderSummary.pricingBreakdown as tier}
                      <tr class="border-b border-hairline">
                        <td class="py-1 text-2xs text-gray-600 sm:text-xs">Records {tier.range}</td>
                        <td class="py-1 text-center text-2xs sm:text-xs">{tier.quantity}</td>
                        <td class="py-1 text-right text-2xs sm:text-xs"
                          >CHF {formatSwissFrancs(tier.unitPrice)}</td
                        >
                        <td class="py-1 text-right text-2xs font-medium sm:text-xs"
                          >CHF {formatSwissFrancs(tier.subtotal)}</td
                        >
                      </tr>
                    {/each}
                  </tbody>
                  <tfoot>
                    <tr class="border-t border-hairline-strong">
                      <td colspan="3" class="py-1 text-2xs text-gray-600 sm:text-xs"
                        >{$_('pricing.subtotal')}</td
                      >
                      <td class="py-1 text-right text-2xs font-medium sm:text-xs"
                        >CHF {formatSwissFrancs($orderSummary.subtotal)}</td
                      >
                    </tr>
                    <tr>
                      <td colspan="3" class="py-1 text-2xs text-gray-600 sm:text-xs"
                        >{$_('pricing.vat')}</td
                      >
                      <td class="py-1 text-right text-2xs font-medium sm:text-xs"
                        >CHF {formatSwissFrancs($orderSummary.vatAmount)}</td
                      >
                    </tr>
                    <tr class="border-t border-hairline-strong">
                      <td colspan="3" class="py-1 text-xs font-bold sm:py-2 sm:text-sm"
                        >{$_('pricing.total')}</td
                      >
                      <td class="py-1 text-right text-xs font-bold text-gray-900 sm:py-2 sm:text-sm"
                        >CHF {formatSwissFrancs($orderSummary.total)}</td
                      >
                    </tr>
                  </tfoot>
                </table>
              </div>
            {:else}
              <div class="py-4 text-center text-gray-500">
                <p class="text-sm">{$_('status.noRecordsSelected')}</p>
                <p class="text-xs">{$_('status.applyFiltersForPricing')}</p>
              </div>
            {/if}
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: FORM -->
    {:else}
      <div class="space-y-3 sm:space-y-4">
        <div>
          <h2 class="mb-2 text-base font-bold text-gray-900 sm:mb-3 sm:text-lg">
            {$_('order.yourDetails')}
          </h2>

          <!-- Form Fields -->
          <div class="space-y-2 sm:space-y-3">
            <div class="group">
              <label class="mb-1 block text-xs font-medium text-gray-700"
                >{$_('form.labels.companyName')}</label
              >
              <input
                type="text"
                name="company"
                placeholder={$_('form.placeholders.companyName')}
                bind:value={customer.company}
                on:blur={() => touchField('company')}
                class="w-full rounded-control border {fieldErrors.company
                  ? 'border-red-500'
                  : 'border-gray-300'} bg-white px-3 py-2 text-sm placeholder-gray-500 transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 {fieldErrors.company
                  ? 'focus:ring-red-200'
                  : 'focus:ring-accent-200'}"
              />
              {#if fieldErrors.company}
                <p class="mt-1 text-xs text-red-600">{fieldErrors.company}</p>
              {/if}
            </div>

            <!-- Name fields -->
            <div class="grid grid-cols-3 gap-1 sm:gap-2">
              <div class="group">
                <label class="mb-1 block text-xs font-medium text-gray-700"
                  >{$_('form.labels.salutation')}</label
                >
                <select
                  bind:value={customer.salutation}
                  class="w-full rounded-control border border-gray-300 bg-white px-3 py-2 text-sm transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-200"
                >
                  <option value="">{$_('buttons.select')}</option>
                  <option value="Mr">{$_('form.salutations.mr')}</option>
                  <option value="Ms">{$_('form.salutations.ms')}</option>
                  <option value="Dr">{$_('form.salutations.dr')}</option>
                </select>
              </div>
              <div class="group">
                <label class="mb-1 block text-xs font-medium text-gray-700"
                  >{$_('form.labels.firstName')}</label
                >
                <input
                  type="text"
                  name="firstName"
                  placeholder={$_('form.placeholders.firstName')}
                  bind:value={customer.firstName}
                  on:blur={() => touchField('firstName')}
                  class="w-full rounded-control border {fieldErrors.firstName
                    ? 'border-red-500'
                    : 'border-gray-300'} bg-white px-3 py-2 text-sm placeholder-gray-500 transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 {fieldErrors.firstName
                    ? 'focus:ring-red-200'
                    : 'focus:ring-accent-200'}"
                />
                {#if fieldErrors.firstName}
                  <p class="mt-1 text-xs text-red-600">{fieldErrors.firstName}</p>
                {/if}
              </div>
              <div class="group">
                <label class="mb-1 block text-xs font-medium text-gray-700"
                  >{$_('form.labels.lastName')}</label
                >
                <input
                  type="text"
                  name="lastName"
                  placeholder={$_('form.placeholders.lastName')}
                  bind:value={customer.lastName}
                  on:blur={() => touchField('lastName')}
                  class="w-full rounded-control border {fieldErrors.lastName
                    ? 'border-red-500'
                    : 'border-gray-300'} bg-white px-3 py-2 text-sm placeholder-gray-500 transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 {fieldErrors.lastName
                    ? 'focus:ring-red-200'
                    : 'focus:ring-accent-200'}"
                />
                {#if fieldErrors.lastName}
                  <p class="mt-1 text-xs text-red-600">{fieldErrors.lastName}</p>
                {/if}
              </div>
            </div>

            <!-- Address fields -->
            <div class="grid grid-cols-4 gap-1 sm:gap-2">
              <div class="group col-span-3">
                <label class="mb-1 block text-xs font-medium text-gray-700"
                  >{$_('form.labels.street')}</label
                >
                <input
                  type="text"
                  name="street"
                  placeholder={$_('form.placeholders.street')}
                  bind:value={customer.street}
                  on:blur={() => touchField('street')}
                  class="w-full rounded-control border {fieldErrors.street
                    ? 'border-red-500'
                    : 'border-gray-300'} bg-white px-3 py-2 text-sm placeholder-gray-500 transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 {fieldErrors.street
                    ? 'focus:ring-red-200'
                    : 'focus:ring-accent-200'}"
                />
                {#if fieldErrors.street}
                  <p class="mt-1 text-xs text-red-600">{fieldErrors.street}</p>
                {/if}
              </div>
              <div class="group">
                <label class="mb-1 block text-xs font-medium text-gray-700"
                  >{$_('form.labels.houseNumber')}</label
                >
                <input
                  type="text"
                  placeholder={$_('form.labels.houseNumber')}
                  bind:value={customer.house}
                  class="w-full rounded-control border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-500 transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-200"
                />
              </div>
            </div>

            <div class="grid grid-cols-3 gap-1 sm:gap-2">
              <div class="group">
                <label class="mb-1 block text-xs font-medium text-gray-700"
                  >{$_('form.labels.zip')}</label
                >
                <input
                  type="text"
                  name="zip"
                  placeholder={$_('form.placeholders.zip')}
                  bind:value={customer.zip}
                  on:blur={() => touchField('zip')}
                  class="w-full rounded-control border {fieldErrors.zip
                    ? 'border-red-500'
                    : 'border-gray-300'} bg-white px-3 py-2 text-sm placeholder-gray-500 transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 {fieldErrors.zip
                    ? 'focus:ring-red-200'
                    : 'focus:ring-accent-200'}"
                />
                {#if fieldErrors.zip}
                  <p class="mt-1 text-xs text-red-600">{fieldErrors.zip}</p>
                {/if}
              </div>
              <div class="group col-span-2">
                <label class="mb-1 block text-xs font-medium text-gray-700"
                  >{$_('form.labels.city')}</label
                >
                <input
                  type="text"
                  name="city"
                  placeholder={$_('form.placeholders.city')}
                  bind:value={customer.city}
                  on:blur={() => touchField('city')}
                  class="w-full rounded-control border {fieldErrors.city
                    ? 'border-red-500'
                    : 'border-gray-300'} bg-white px-3 py-2 text-sm placeholder-gray-500 transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 {fieldErrors.city
                    ? 'focus:ring-red-200'
                    : 'focus:ring-accent-200'}"
                />
                {#if fieldErrors.city}
                  <p class="mt-1 text-xs text-red-600">{fieldErrors.city}</p>
                {/if}
              </div>
            </div>

            <!-- Country field -->
            <div class="group">
              <label class="mb-1 block text-xs font-medium text-gray-700"
                >{$_('form.labels.country')}</label
              >
              <select
                name="country"
                bind:value={customer.country}
                class="w-full appearance-none rounded-control border border-gray-300 bg-white px-3 py-2 text-sm transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-200"
                style="background-image: url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e'); background-repeat: no-repeat; background-position: right 0.5rem center; background-size: 1.5em 1.5em; padding-right: 2.5rem;"
              >
                {#each countries as country}
                  <option value={country.code}>
                    {country.flag}
                    {country.name}
                  </option>
                {/each}
              </select>
            </div>

            <div class="group">
              <label class="mb-1 block text-xs font-medium text-gray-700"
                >{$_('form.labels.email')}</label
              >
              <input
                type="email"
                name="email"
                placeholder={$_('form.placeholders.email')}
                bind:value={customer.email}
                on:blur={() => touchField('email')}
                class="w-full rounded-control border {fieldErrors.email
                  ? 'border-red-500'
                  : 'border-gray-300'} bg-white px-3 py-2 text-sm placeholder-gray-500 transition-colors focus:border-accent-500 focus:outline-none focus:ring-2 {fieldErrors.email
                  ? 'focus:ring-red-200'
                  : 'focus:ring-accent-200'}"
              />
              {#if fieldErrors.email}
                <p class="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
              {/if}
            </div>
          </div>
        </div>

        <!-- Terms Checkbox -->
        <label class="group flex cursor-pointer items-start">
          <input
            type="checkbox"
            bind:checked={customer.agreed}
            class="mt-1 h-4 w-4 rounded border-gray-300 text-accent-600 focus:ring-2 focus:ring-accent-200"
          />
          <span class="ml-3 text-sm text-gray-700 transition-colors group-hover:text-gray-900">
            {$_('form.labels.agreed')}
            <a
              href="https://www.ainova.ch/terms-of-service"
              class="font-medium text-accent-600 underline hover:text-accent-800"
              target="_blank">{$_('form.labels.terms')}</a
            >.
          </span>
        </label>
      </div>
    {/if}

    <svelte:fragment slot="footer">
      <button
        type="button"
        on:click={() => (step === 1 ? cancelOrder() : (step = 1))}
        class="rounded-control border border-hairline px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-hairline-strong hover:text-gray-900"
      >
        {step === 1 ? $_('buttons.cancel') : $_('guided.back')}
      </button>

      {#if step === 1}
        <button
          type="button"
          class="rounded-control bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700"
          on:click={() => (step = 2)}
        >
          {$_('guided.next')}
        </button>
      {:else}
        <button
          type="button"
          class="rounded-control bg-accent-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
          disabled={!isFormValid || isSubmitting}
          on:click={showOrderConfirmation}
        >
          {#if isSubmitting}
            <svg class="mr-2 inline h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing...
          {:else}
            {$_('buttons.confirmOrder')}
          {/if}
        </button>
      {/if}
    </svelte:fragment>
  </ScreenView>
{/if}
